import { computed, ref } from 'vue';
import { RouteLocationNormalized } from 'vue-router';

interface JobCompliance {
  demographic_data_consent_applies: boolean;
  requires_consent: boolean;
  requires_processing_consent: boolean;
  requires_retention_consent: boolean;
  retention_period: number | null;
  type: string;
}

interface JobDepartment {
  child_ids: number[];
  id: number;
  name: string;
  parent_id: number | null;
}

interface JobLocation {
  name: string;
}

interface JobOffice {
  child_ids: number[];
  id: number;
  location: string;
  name: string;
  parent_id: number | null;
}

interface Job {
  absolute_url: string;
  company_name: string;
  content: string;
  data_compliance: JobCompliance[];
  departments: JobDepartment[];
  id: number;
  internal_job_id: number;
  location: JobLocation;
  metadata: any | null;
  offices: JobOffice[];
  requisition_id: string | null;
  title: string;
  updated_at: string;
}

export interface JobsResponse {
  jobs: Job[];
  meta: {
    total: number;
  };
}

/**
 * This composable is used to fetch and display job postings.
 * It groups jobs by location and team, and sorts them by location.
 * It also provides a selected job by ID.
 *
 * @param route - The reactive object returned by `useRoute()`
 */
export function useJobPostings(route: RouteLocationNormalized) {
  const jobs = ref<Job[]>([]);
  const groups = computed(() => {
    const location = new Map<string, Job[]>();
    const team = new Map<string, Job[]>();

    jobs.value.forEach((job) => {
      const locationName = job.location.name.trim();
      if (!location.has(locationName)) {
        location.set(locationName, []);
      }
      location.get(locationName)?.push(job);

      const teamName = job.departments[0].name;
      if (!team.has(teamName)) {
        team.set(teamName, []);
      }
      team.get(teamName)?.push(job);
    });

    return { location, team };
  });

  const sortBy = ref<keyof typeof groups.value>('location');
  const sortedJobGroups = computed<{ key: string; jobs: Job[] }[]>(() => {
    const group = groups.value[sortBy.value];
    const keys = Array.from(group.keys()).sort();
    return keys.map((key) => ({ key, jobs: group.get(key)! }));
  });

  const selectedJob = computed(() => {
    const { position } = route.query;
    if (typeof position !== 'string' || !/^\d+$/.test(position)) {
      return undefined;
    }
    return jobs.value.find((job) => job.id === Number(position));
  });

  return {
    groups,
    jobs,
    async loadJobs() {
      jobs.value = [];
      const response = await fetch('https://api.greenhouse.io/v1/boards/nutscom/jobs?content=true');
      const data: JobsResponse = await response.json();
      jobs.value = data.jobs;
    },
    selectedJob,
    sortBy,
    sortedJobGroups,
  };
}
